<template>
  <NavBar />
  <div class="blog-list">
    <h1 class="blog-title">Blog</h1>
    <!-- 动态分类选择器 -->
    <div class="category-selector">
      <button
        v-for="category in categories"
        :key="category"
        :class="{ active: selectedCategory === category }"
        @click="selectCategory(category)"
        class="category-btn"
      >
        {{ category === "all" ? "全部" : category.toUpperCase() }}
      </button>
    </div>
    <div
      v-for="post in sortedBlogMeta"
      :key="post.id"
      class="blog-item"
      @click="goToDetail(post)"
    >
      <div class="blog-img-smart">
        <img :src="coverMap[post.id] || post.image" class="blog-img-bg" />
        <img :src="coverMap[post.id] || post.image" class="blog-img-fore" />
      </div>
      <div class="blog-content">
        <div class="blog-item-title">{{ post.title }}</div>
        <div class="blog-meta-row">
          <div class="blog-tags">
            <span v-for="tag in post.tags" :key="tag" class="blog-tag">{{
              tag
            }}</span>
          </div>
          <div class="blog-item-date">{{ post.date }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import NavBar from "@/components/NavBar.vue";
import { useRouter } from "vue-router";
const coverMap = ref({});
const blogMeta = ref([]);
const categories = ref([]); // 动态分类列表
const selectedCategory = ref("all"); // 当前选中的分类
const router = useRouter();

function goToDetail(post) {
  router.push({
    name: "BlogDetail",
    params: { id: post.id },
    query: {
      filePath: post.mdFile,
      title: post.title,
    },
  });
}

// 动态发现所有分类（币种目录）
// async function discoverCategories() {
//   try {
//     const response = await fetch("/blogs/");
//     const text = await response.text();
//     const parser = new DOMParser();
//     const doc = parser.parseFromString(text, "text/html");

//     // 获取所有目录链接（以/结尾的）
//     // 获取所有链接，然后过滤出包含/reports/的
//     const allLinks = Array.from(doc.querySelectorAll("a"));
//     // const dirLinks = allLinks
//     //   .filter((link) => link.getAttribute("href").includes("/blogs/"))
//     //   .map((link) => {
//     //     const href = link.getAttribute("href");
//     //     return href.split("/").pop(); // 获取最后一部分
//     //   });

//     const categories = new Set(["all"]); // 始终包含"全部"

//     for (const link of allLinks) {
//       const href = link.getAttribute("href");

//       if (href.includes("/blogs/")) {
//         if (href.endsWith("/")) {
//           const parts = href.split("/").filter((part) => part);
//           // console.log("路径分解:", parts, "来自:", href);
//           if (parts.length >= 2) {
//             const categoryName = parts[parts.length - 1];
//             // console.log("提取分类名:", categoryName);
//             if (categoryName !== "blogs") {
//               categories.add(categoryName);
//             }
//           }
//         } else if (href.endsWith(".md") && href.split("/").length === 3) {
//           // 根目录文件：/blogs/文件.md (路径部分数为3)
//           categories.add("root");
//         }
//       }
//     }

//     categories.value = Array.from(categories);
//     // console.log("发现的所有分类:", categories.value);
//     // console.log("默认选中分类:", selectedCategory.value);

//     // categories.value = [...dirLinks]; //
//   } catch (error) {
//     console.error("发现分类失败:", error);
//     console.error("请求URL:", "/blogs/");
//     console.error("错误详情:", error.message);
//     categories.value = ["all"]; // 默认只有全部选项
//   }
// }

// 统一的封面处理函数
async function processCoverImages(posts) {
  const displayOrder = [...posts].sort((a, b) => b.date.localeCompare(a.date));

  for (const post of displayOrder) {
    if (post.mdFile) {
      // const decodedPath = decodeURIComponent(post.mdFile);
      // const img = await getFirstImage(decodedPath);
      // coverMap.value[post.id] = img || post.image;
      coverMap.value[post.id] = post.image;
    } else {
      coverMap.value[post.id] = post.image;
    }
  }
}

async function discoverCategories() {
  try {
    const response = await fetch("/blogs/"); // 获取目录列表
    const text = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");

    const allLinks = Array.from(doc.querySelectorAll("a")); // 获取所有链接

    const categories = new Set(["all"]); // 始终包含"全部"

    // 遍历所有链接
    for (const link of allLinks) {
      const href = link.getAttribute("href");

      // 只筛选出目录（以/结尾）或者根目录下的 .md 文件
      if (href.endsWith("/")) {
        const categoryName = href.split("/").filter(Boolean).pop(); // 获取目录名
        if (categoryName !== "blogs") { // 排除不必要的部分
          categories.add(categoryName);
        }
      } else if (href.endsWith(".md")) {
        categories.add("root"); // 根目录的 .md 文件加入"root"分类
      }
    }

    categories.value = Array.from(categories); // 更新分类列表
  } catch (error) {
    console.error("发现分类失败:", error);
    categories.value = ["all"]; // 默认只有全部选项
  }
}

async function discoverBlogs() {
  try {
    const basePath =
      selectedCategory.value === "all"
        ? "/blogs/"
        : `/blogs/${selectedCategory.value}/`;

    const response = await fetch(basePath); // 获取分类下的文件列表
    const text = await response.text();

    const parser = new DOMParser();
    const doc = parser.parseFromString(text, "text/html");
    const links = doc.querySelectorAll('a[href$=".md"]'); // 获取所有 .md 文件链接

    const blogs = [];
    let id = 1;

    for (const link of links) {
      const fullPath = link.getAttribute("href"); // 获取文件路径

      // 拼接完整路径
      const filePath = fullPath.startsWith("/") ? fullPath : `${basePath}${fullPath}`;
      const encodedFileName = filePath.split("/").pop();
      const fileName = decodeURIComponent(encodedFileName);

      try {
        const title = extractTitle(fileName);
        const date = extractDate(fileName);
        const tags = extractTags(fileName);
        const image =
          extractImage(fileName) || require("@/assets/logo.png");

        const blogData = {
          id: id++,
          title,
          date,
          image,
          tags: tags.length ? tags : ["区块链"],
          mdFile: filePath, // 文件路径
          category: selectedCategory.value, // 分类
        };
        blogs.push(blogData);
      } catch (error) {
        console.warn(`处理文件 ${fileName} 失败:`, error);
      }
    }

    blogMeta.value = blogs; // 更新博客列表
    await processCoverImages(blogs); // 处理封面图
  } catch (error) {
    console.error("发现博客失败:", error);
  }
}


// 动态发现博客（根据选中分类）
// async function discoverBlogs() {
//   try {
//     const basePath =
//       selectedCategory.value === "all"
//         ? "/blogs/"
//         : `/blogs/${selectedCategory.value}/`;
//     // console.log("selectedCategory:", selectedCategory.value);
//     // console.log("basePath:", basePath);
//     const response = await fetch(basePath);
//     const text = await response.text();

//     const parser = new DOMParser();
//     const doc = parser.parseFromString(text, "text/html");
//     const links = doc.querySelectorAll('a[href$=".md"]');

//     const blogs = [];
//     let id = 1;

//     for (const link of links) {
//       const fullPath = link.getAttribute("href");
//       const encodedFileName = fullPath.split("/").pop();
//       const fileName = decodeURIComponent(encodedFileName);
//       const filePath = fullPath; // 直接使用完整的href路径

//       try {
//         const contentResponse = await fetch(filePath);
//         // console.log("响应URL:", contentResponse.url);
//         // console.log("响应状态:", contentResponse.status);

//         const content = await contentResponse.text();
//         // console.log("原始内容长度:", content.length);
//         // console.log("内容预览:", content.substring(0, 200));

//         const title = extractTitle(content, fileName);
//         // console.log("提取的标题:", title);
//         const date = extractDate(content, fileName);
//         const tags = extractTags(content, fileName);
//         const image =
//           extractImage(content, fileName) || require("@/assets/logo.png");

//         // 添加分类信息
//         const category =
//           selectedCategory.value === "all"
//             ? basePath.split("/").filter(Boolean).pop()
//             : selectedCategory.value;

//         const blogData = {
//           id: id++,
//           title,
//           date,
//           image,
//           tags: tags.length ? tags : ["区块链"],
//           mdFile: filePath,
//           category,
//         };
//         blogs.push(blogData);
//         // console.log("存储的文章:", blogData);
//       } catch (error) {
//         console.warn(`处理文件 ${fileName} 失败:`, error);
//       }
//     }

//     blogMeta.value = blogs;
//     await processCoverImages(blogs); // 切换分类时更新封面
//   } catch (error) {
//     console.error("发现博客失败:", error);
//   }
// }

// 提取标题 - 直接从文件名获取（去掉.md后缀）
function extractTitle(fileName) {
  return fileName.replace(".md", "");
}

// 提取日期 - 从文件名中提取日期并格式化
function extractDate(fileName) {
  // 优先匹配8位数字日期（YYYYMMDD格式）
  const fullDateMatch = fileName.match(/(\d{8})/);

  if (fullDateMatch && fullDateMatch[1]) {
    const dateStr = fullDateMatch[1];
    // 格式化为 YYYY-MM-DD
    return `${dateStr.substring(0, 4)}-${dateStr.substring(
      4,
      6
    )}-${dateStr.substring(6, 8)}`;
  }

  // 如果没有8位日期，尝试匹配4位数字（MMDD格式）
  const shortDateMatch = fileName.match(/(\d{4})/);
  if (shortDateMatch && shortDateMatch[1]) {
    const dateStr = shortDateMatch[1];
    //当前年份为2026年，老文件不做更新，所以设置为2025年开头
    // const currentYear = new Date().getFullYear(); 
    const currentYear = 2025;
    // 格式化为 YYYY-MM-DD，使用当前年份
    return `${currentYear}-${dateStr.substring(0, 2)}-${dateStr.substring(
      2,
      4
    )}`;
  }

  // 如果都没有找到日期，使用当前日期
  return new Date().toISOString().split("T")[0];
}

// 提取标签 - 根据文件名规则判断
function extractTags(fileName) {
  // 如果是"加密日报"类型的文件
  if (fileName.includes("加密日报")) {
    return ["区块链"];
  }

  // 对于"代币名称_tag_日期.md"格式的文件
  const parts = fileName.replace(".md", "").split("_");

  if (parts.length >= 2) {
    // 第二个部分就是tag
    return [parts[1]];
  }

  // 默认标签
  return ["区块链"];
}

// 提取图片 - 根据文件类型返回不同的默认图片
function extractImage(fileName) {
  // 如果是"加密日报"类型，返回日报专用图片
  if (fileName.includes("加密日报")) {
    return require("@/assets/daily.jpg");
  }

  // 其他类型使用默认logo
  return require("@/assets/logo.png");
}

// 切换分类
function selectCategory(category) {
  selectedCategory.value = category;
  discoverBlogs(); // 重新加载该分类的博客
}

// 日期倒序排序
const sortedBlogMeta = computed(() => {
  return [...blogMeta.value].sort((a, b) => b.date.localeCompare(a.date));
});

// 新增：提取第一张图片
// async function getFirstImage(mdPath) {
//   try {
//     // 将静态路径转换为动态路径
//     let actualPath = mdPath;
//     // if (mdPath.startsWith("/blogs/")) {
//     //   actualPath = mdPath.replace("/blogs/", "/reports/blogs/");
//     // }
//     // actualPath;
//     const res = await fetch(actualPath);
//     const text = await res.text();

//     const mdImg = text.match(/!\[.*?\]\((.*?)\)/);
//     if (mdImg && mdImg[1]) return mdImg[1];

//     const htmlImg = text.match(/<img.*?src=["'](.*?)["']/);
//     if (htmlImg && htmlImg[1]) return htmlImg[1];

//     return null;
//   } catch {
//     return null;
//   }
// }

// 页面加载时执行
onMounted(async () => {
  await discoverCategories(); // 先发现所有分类
  await discoverBlogs(); // 然后加载博客

  // ... 原有的封面图片处理逻辑
  // 按照显示顺序（日期倒序）处理封面
  // const displayOrder = [...blogMeta.value].sort((a, b) =>
  //   b.date.localeCompare(a.date)
  // );

  // for (const post of displayOrder) {
  //   if (post.mdFile) {
  //     const decodedPath = decodeURIComponent(post.mdFile);
  //     console.log("post.mdFile:", post);
  //     console.log("decodedPath:", decodedPath);
  //     const img = await getFirstImage(decodedPath);
  //     coverMap.value[post.id] = img || post.image;
  //   } else {
  //     coverMap.value[post.id] = post.image;
  //   }
  // }
  await processCoverImages(blogMeta.value);
});
</script>

<style scoped>
.blog-list {
  max-width: 700px;
  margin: 40px auto; /* 上下40px，左右自动，整体居中 */
  padding: 0 16px;
}
.blog-title {
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 32px;
  text-align: left;
}
.blog-item {
  display: flex;
  align-items: center;
  gap: 40px; /* 图片和内容间距更大 */
  margin-bottom: 40px; /* 每项之间更宽松 */
  cursor: pointer;
  border-radius: 20px;
  transition: background 0.15s, box-shadow 0.15s;
  padding: 16px 24px; /* 内边距更大 */
}
.blog-item:hover {
  background: #f7f8fa;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.04);
}
.blog-img {
  width: 180px;
  height: 112px;
  object-fit: contain;
  border-radius: 20px;
  border: 1px solid #eee;
  flex-shrink: 0;
}

.blog-img-smart {
  position: relative;
  width: 180px;
  height: 112px;
  border-radius: 20px;
  overflow: hidden;
  flex-shrink: 0;
}
.blog-img-bg {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: blur(12px) brightness(0.9);
  transform: scale(1.1);
  z-index: 1;
}
.blog-img-fore {
  position: relative;
  width: 100%;
  height: 100%;
  object-fit: contain;
  z-index: 2;
}

.blog-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.blog-item-title {
  font-size: 1.25rem;
  font-weight: bold;
  color: #111;
  margin-bottom: 2px;
}
.blog-item-date {
  color: #888;
  font-size: 1rem;
  margin-bottom: 6px;
}
.blog-tags {
  display: flex;
  gap: 8px;
  margin-top: 2px;
}
.blog-tag {
  background: #f3f4f6;
  color: #888;
  font-size: 0.95rem;
  border-radius: 8px;
  padding: 2px 10px;
  font-weight: 500;
  white-space: nowrap;
}

/* 分类选择器样式 */
.category-selector {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.category-btn {
  padding: 8px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: white;
  cursor: pointer;
  transition: all 0.2s;
}

.category-btn:hover {
  background: #f9fafb;
}

.category-btn.active {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

/* 卡片式布局，移动端专用 */
@media (max-width: 700px) {
  .blog-list {
    max-width: 100vw;
    padding: 0 8px;
  }
  .blog-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 20px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.06);
    padding: 16px 14px 14px 14px;
    cursor: pointer;
    transition: box-shadow 0.15s;
  }
  .blog-img-smart {
    width: 100%;
    height: 0;
    padding-bottom: 56%; /* 16:9比例 */
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 8px;
  }
  .blog-img-bg,
  .blog-img-fore {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 12px;
  }
  .blog-img-bg {
    filter: blur(10px) brightness(0.9);
    z-index: 1;
  }
  .blog-img-fore {
    z-index: 2;
    object-fit: contain;
  }
  .blog-content {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .blog-item-title {
    font-size: 1.1rem;
    font-weight: bold;
    color: #111;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin-bottom: 2px;
  }
  .blog-meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: 8px;
  }
  .blog-tags {
    display: flex;
    gap: 6px;
  }
  .blog-tag {
    background: #f3f4f6;
    color: #888;
    font-size: 0.92rem;
    border-radius: 8px;
    padding: 2px 8px;
    font-weight: 500;
    white-space: nowrap;
  }
  .blog-item-date {
    color: #b0b0b0;
    font-size: 0.92rem;
    white-space: nowrap;
  }
}
</style>
